import type { ReactNode } from 'react';
import PageBack from './PageBack';

/**
 * The legal/system-page shell and body style: the Back row, a serif title and
 * its dateline on paper, then sans prose in numbered H2 sections. /privacy,
 * /terms, /safety, /delete-account and /child-safety are the five pages on it (the first
 * two moved here 2026-09-30, a markup-only change — docs/legal-version-bump.md:
 * no version bump for one).
 *
 * These pages are not part of the footer-carrying set (docs/frontend-ui-standard.md
 * §3 "Site footer"), so a page using this opens with PageBack and ends with its
 * own content — no footer.
 */
const SANS = '"STK Bureau Sans", Inter, system-ui, sans-serif';
// one title face across the site (2026-09-06 走查)
const TITLE_SERIF = '"Iowan Old Style", "Palatino Linotype", "Book Antiqua", Palatino, "Hoefler Text", Georgia, serif';

export function LegalPage({ title, dateline, children }: { title: string; dateline: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-[#f9f5e9] text-ink">
      <div className="eh-page-top mx-auto w-full max-w-3xl flex-1 px-6 pb-16 sm:px-10">
        <div className="eh-page-back-row">
          <PageBack />
        </div>
        <header className="mb-10">
          <h1 className="mb-2 text-3xl tracking-tight" style={{ fontFamily: TITLE_SERIF, color: '#281d16' }}>
            {title}
          </h1>
          <p className="text-sm text-ink/60">{dateline}</p>
        </header>
        {children}
      </div>
    </div>
  );
}

export function LegalProse({ children }: { children: ReactNode }) {
  return (
    <div
      className="space-y-4 text-base leading-relaxed text-ink/90 [&_a]:text-oriOrange [&_a]:underline [&_h3]:mt-6 [&_h3]:font-semibold [&_li]:my-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6"
      style={{ fontFamily: SANS }}
    >
      {children}
    </div>
  );
}

export function LegalSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="mb-4 text-lg font-semibold" style={{ fontFamily: SANS, color: '#3A331D' }}>
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}
